import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useContent } from "@/lib/content-context";
import { motion, AnimatePresence } from "framer-motion";
import CafeHideoutLogo from "@/components/CafeHideoutLogo";
import { fileUrl } from "@/lib/api";

export default function Navbar({ transparent = false }) {
    const { content } = useContent();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const allLinks = content?.hero?.nav_links || [
        { label: "Home", href: "/" },
        { label: "About", href: "/about" },
        { label: "Our Cafes", href: "/our-cafes" },
        { label: "Partner with Us", href: "/partner" },
        { label: "Media & Blog", href: "/blog" },
        { label: "Contact", href: "/contact" },
    ];

    const half = Math.ceil(allLinks.length / 2);
    const leftLinks = allLinks.slice(0, half);
    const rightLinks = allLinks.slice(half);

    const onLight = !transparent || scrolled;
    const logoUrl = content?.brand?.logo_url;
    const logoSize = onLight ? 85 : 106;
    // half-width + a comfortable visual gap on each side
    const logoGap = logoSize / 2 + 28;

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                onLight ? "bg-cream-light/95 backdrop-blur-md border-b border-coffee/8" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1480px] mx-auto px-6 md:px-12 py-2 md:py-3 flex items-center gap-2 relative">
                {/* Left nav (desktop only) */}
                <nav
                    className="hidden md:flex flex-1 items-center justify-end gap-8 lg:gap-10"
                    style={{ paddingRight: 28 }}
                >
                    {leftLinks.map((l) => (
                        <NavLink
                            key={l.href}
                            to={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            className={({ isActive }) =>
                                `tracked text-[11px] gold-underline transition-colors duration-300 whitespace-nowrap ${
                                    onLight ? "text-coffee" : "text-cream"
                                } ${isActive ? "text-gold" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Centered logo
                    - Desktop: stays in flex flow between left/right navs
                    - Mobile: absolutely centered, larger, with breathing room */}
                <Link
                    to="/"
                    data-testid="logo-link"
                    aria-label="Cafe Hideout home"
                    className="flex-shrink-0 flex items-center justify-center transition-transform duration-300 hover:scale-[1.04]
                        absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2
                        md:static md:translate-x-0 md:translate-y-0"
                >
                    <span
                        className="block"
                        style={{ width: logoSize, height: logoSize }}
                    >
                        {logoUrl ? (
                            <span
                                aria-hidden
                                className="block w-full h-full transition-all duration-500"
                                style={{
                                    backgroundColor: onLight ? "#1a0f00" : "#f5e6c8",
                                    WebkitMaskImage: `url(${fileUrl(logoUrl)})`,
                                    maskImage: `url(${fileUrl(logoUrl)})`,
                                    WebkitMaskRepeat: "no-repeat",
                                    maskRepeat: "no-repeat",
                                    WebkitMaskPosition: "center",
                                    maskPosition: "center",
                                    WebkitMaskSize: "contain",
                                    maskSize: "contain",
                                    filter: onLight ? "none" : "drop-shadow(0 2px 6px rgba(0,0,0,0.35))",
                                }}
                            />
                        ) : (
                            <CafeHideoutLogo size={logoSize} tone={onLight ? "coffee" : "cream"} className="transition-all duration-500" />
                        )}
                    </span>
                </Link>

                {/* Mobile spacer so the bar gets the right height (logo is absolute on mobile) */}
                <div
                    className="md:hidden flex-1"
                    style={{ height: logoSize }}
                    aria-hidden
                />

                {/* Right nav (desktop only) */}
                <nav
                    className="hidden md:flex flex-1 items-center justify-start gap-8 lg:gap-10"
                    style={{ paddingLeft: 28 }}
                >
                    {rightLinks.map((l) => (
                        <NavLink
                            key={l.href}
                            to={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                            className={({ isActive }) =>
                                `tracked text-[11px] gold-underline transition-colors duration-300 whitespace-nowrap ${
                                    onLight ? "text-coffee" : "text-cream"
                                } ${isActive ? "text-gold" : ""}`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                {/* Mobile menu toggle (right) */}
                <button
                    onClick={() => setOpen((o) => !o)}
                    aria-label="menu"
                    data-testid="mobile-menu-toggle"
                    className={`md:hidden absolute right-6 top-1/2 -translate-y-1/2 ${onLight ? "text-coffee" : "text-cream"}`}
                >
                    {open ? <X size={24} /> : <Menu size={24} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, x: "100%" }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: "100%" }}
                        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                        className="md:hidden fixed top-0 right-0 h-screen w-[82%] bg-coffee text-cream p-10 pt-24 z-40 overflow-y-auto"
                    >
                        <nav className="flex flex-col gap-5">
                            {allLinks.map((l) => (
                                <Link
                                    key={l.href}
                                    to={l.href}
                                    onClick={() => setOpen(false)}
                                    data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
                                    className="font-editorial font-bold text-3xl"
                                >
                                    {l.label}
                                </Link>
                            ))}
                        </nav>
                        <div className="absolute bottom-10 left-10 right-10 tracked text-[10px] text-cream/60">
                            Bengaluru · Mumbai · Pune
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </header>
    );
}
